import type { HotelListOptions } from '@bandido/shared/types/hotels/hotel-search-results';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorCode } from '@frontend/features/travel-services/queries';
import {
  emptyHotelSnapshot,
  HotelSearchSnapshot,
  HotelSearchTransport,
  runHotelSearch,
  readHotelSearchResults,
} from './hotelSearchFlow';
import type { HotelSearchFilters } from '@bandido/shared/types/hotels/hotel-search-filters';
import { HOTEL_POLL_TIMEOUT_MS, HotelSearchDraft, normalizedHotelFilters } from './searchModel';

function pause(signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error('stopped'));
      return;
    }
    const abort = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      reject(new Error('stopped'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, 1500);
    signal.addEventListener('abort', abort, { once: true });
  });
}
export function useHotelSearch(transport: HotelSearchTransport) {
  const [snapshot, setSnapshot] = useState<HotelSearchSnapshot | null>(null);
  const [error, setError] = useState<
    'unmapped' | 'refreshStopped' | 'contextChanged' | 'contextUnresolved' | 'routeConfiguration' | 'error' | null
  >(null);
  const [committed, setCommitted] = useState<HotelSearchDraft | null>(null);
  const current = useRef<{ generation: number; abort: AbortController; timer: ReturnType<typeof setTimeout> } | null>(
    null,
  );
  const generation = useRef(0);
  const startedAt = useRef(0);
  const visiblePages = useRef(1);

  const cancel = useCallback(() => {
    generation.current += 1;
    if (current.current) {
      clearTimeout(current.current.timer);
      current.current.abort.abort();
      current.current = null;
    }
  }, []);
  useEffect(() => cancel, [cancel]);
  const start = useCallback(
    (draft: HotelSearchDraft, offset = 0, previous?: HotelSearchSnapshot, restartRead = false) => {
      cancel();
      if (!previous) {
        startedAt.current = Date.now();
        visiblePages.current = 1;
      }
      if (previous && restartRead) startedAt.current = Date.now();
      const input: HotelSearchDraft = JSON.parse(JSON.stringify(draft));
      const id = generation.current,
        abort = new AbortController();
      const isCurrent = () => generation.current === id;
      input.filters = normalizedHotelFilters(input.filters);
      input.results ??= { sort: 'selection' };
      setCommitted(input);
      setError(null);
      setSnapshot(
        previous
          ? {
              ...previous,
              phase: 'loading',
            }
          : { ...emptyHotelSnapshot },
      );
      const timer = setTimeout(
        () => {
          if (!isCurrent()) return;
          abort.abort();
          setSnapshot((previous) => (previous ? { ...previous, phase: 'expired' } : null));
        },
        previous?.search?.status === 'completed' || previous?.search?.status === 'failed'
          ? HOTEL_POLL_TIMEOUT_MS
          : Math.max(0, HOTEL_POLL_TIMEOUT_MS - (Date.now() - startedAt.current)),
      );
      current.current = { generation: id, abort, timer };
      const context = {
        transport,
        signal: abort.signal,
        isCurrent,
        // The server orders the entire Search before pagination. Never retain an older
        // visual ordering that could put newly received cheaper hotels on later pages.
        publish: (next: HotelSearchSnapshot) => setSnapshot(next),
        pages: () => visiblePages.current,
        onPagesRead: (count: number) => {
          if (isCurrent()) visiblePages.current = count;
        },
        pause: () => pause(abort.signal),
      };
      const work = previous?.search
        ? readHotelSearchResults({
            ...context,
            page: { hotels: previous.hotels, nextOffset: previous.nextOffset },
            search: previous.search,
            filters: input.filters,
            options: input.results ?? { sort: 'selection' },
            startedAt: startedAt.current,
          })
        : runHotelSearch({ ...context, draft: input, offset });
      void work
        .catch((reason) => {
          if (!isCurrent() || abort.signal.aborted) return;
          const code = apiErrorCode(reason);
          const revisionChanged =
            code === 'HOTEL_RESULTS_CHANGED' || (reason instanceof Error && reason.message === 'HOTEL_RESULTS_CHANGED');
          const contextChanged =
            [
              'hotel_context_expired',
              'hotel_context_policy_revoked',
              'hotel_context_request_changed',
              'hotel_search_policy_disabled',
            ].includes(code ?? '') ||
            (reason instanceof Error && reason.message === 'HOTEL_SEARCH_CONTEXT_REQUIRED');
          setError(
            code === 'hotel_route_configuration_invalid'
              ? 'routeConfiguration'
              : code === 'hotel_context_dispatch_unresolved'
                ? 'contextUnresolved'
                : contextChanged
                  ? 'contextChanged'
                  : revisionChanged
                    ? 'refreshStopped'
                    : code === 'HOTEL_DESTINATION_NOT_MAPPED'
                      ? 'unmapped'
                      : 'error',
          );
          setSnapshot((previous) => (previous ? { ...previous, phase: 'failed' } : null));
        })
        .finally(() => {
          clearTimeout(timer);
        });
    },
    [cancel, transport],
  );
  const stop = useCallback(() => {
    cancel();
    setSnapshot((previous) => (previous ? { ...previous, phase: 'stopped' } : null));
  }, [cancel]);
  const loadMore = () => {
    if (!snapshot?.search || !snapshot.resultMeta?.next_cursor || !committed || snapshot.phase === 'loading') return;
    visiblePages.current += 1;
    start(committed, 0, snapshot);
  };
  return {
    snapshot,
    loadMore,
    error,
    committed,
    start,
    stop,
    reloadResults: () => {
      if (committed && snapshot?.search) start(committed, 0, snapshot, true);
    },
    changeOptions: (results: HotelListOptions) => {
      if (!committed || !snapshot?.search || ['expired', 'stopped'].includes(snapshot.phase)) return;
      visiblePages.current = 1;
      start({ ...committed, results }, 0, { ...snapshot, cards: [], resultMeta: undefined, totalCount: undefined });
    },
    changeFilters: (filters: HotelSearchFilters) => {
      if (!committed || !snapshot?.search || ['expired', 'stopped'].includes(snapshot.phase)) return;
      visiblePages.current = 1;
      start({ ...committed, filters }, 0, { ...snapshot, cards: [], resultMeta: undefined, totalCount: undefined });
    },
    busy: snapshot?.phase === 'loading' || snapshot?.phase === 'running',
  };
}
